import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import {
  DollarSign, TrendingDown, Zap, Cloud, FileText, Loader2,
  AlertTriangle, CheckCircle, Database, Cpu, RefreshCw,
  ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import { api } from '../../utils/api';
import {
  CHART_PALETTE,
  CHART_DONE_COLOR,
  CHART_NEGATIVE_COLOR,
  CHART_AXIS_STYLE,
  CHART_GRID_STYLE,
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_LEGEND_STYLE,
  getSeriesColor,
} from '../../config/chartTheme';

// Updated color scheme for tiers using brand palette
const TIER_COLORS: Record<string, string> = {
  pdfplumber: CHART_DONE_COLOR,        // Green - Free
  tesseract: CHART_PALETTE[1],         // Gray 400
  tesseract_validated: CHART_PALETTE[0], // Brand orange
  paddleocr: CHART_PALETTE[2],         // Gray 500
  csv_adapter: CHART_PALETTE[3],       // Gray 600
  excel_adapter: CHART_PALETTE[4],     // Gray 300
  ofx_parser: CHART_PALETTE[5],        // Gray 700
  claude_ocr: CHART_PALETTE[0],        // Brand orange - Paid
  textract: CHART_PALETTE[1],          // Gray 400 - Paid
  unknown: CHART_NEGATIVE_COLOR,       // Muted gray
};

const TIER_LABELS: Record<string, string> = {
  pdfplumber: 'PDFPlumber (Free)',
  tesseract: 'Tesseract (Free)',
  tesseract_validated: 'Tesseract+Validation ($0.001)',
  paddleocr: 'PaddleOCR (Free)',
  csv_adapter: 'CSV Parser (Free)',
  excel_adapter: 'Excel Parser (Free)',
  ofx_parser: 'OFX Parser (Free)',
  claude_ocr: 'Claude OCR (~$0.04)',
  textract: 'AWS Textract (~$0.05)',
};

interface TierStats {
  count: number;
  cost: number;
}

interface DailyData {
  date: string;
  count: number;
  cost: number;
}

interface CaseExtractionData {
  case_id: string;
  client_name: string;
  total_files: number;
  total_cost: number;
  methods_used: Record<string, number>;
  problem_files: number;
  success_rate: number;
}

interface ExtractionAnalyticsData {
  total_uploads: number;
  total_cost_usd: number;
  cost_by_tier: Record<string, TierStats>;
  cost_by_day: DailyData[];
  avg_cost_per_upload: number;
  free_tier_percentage: number;
  paid_tier_percentage: number;
  period_days: number;
  by_case?: CaseExtractionData[];
  file_type_breakdown?: Record<string, { count: number; avg_cost: number; problem_rate: number }>;
}

interface ExtractionAnalyticsProps {
  days?: number;
}

export default function ExtractionAnalytics({ days = 30 }: ExtractionAnalyticsProps) {
  const [data, setData] = useState<ExtractionAnalyticsData | null>(null);
  const [caseData, setCaseData] = useState<CaseExtractionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCaseBreakdown, setShowCaseBreakdown] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(days);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [analyticsResponse, caseResponse] = await Promise.all([
        api.get(`/api/v1/upload-savefirst/analytics/extraction-costs?days=${selectedPeriod}`),
        api.get(`/api/v1/upload-savefirst/analytics/extraction-by-case?days=${selectedPeriod}`).catch(() => ({ cases: [] }))
      ]);
      setData(analyticsResponse);
      setCaseData(caseResponse.cases || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load extraction analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPeriod]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[var(--color-border)] p-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-brand)]" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-xl border border-[var(--color-border)] p-8">
        <div className="text-center text-[var(--color-text-muted)] py-8">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-[var(--color-brand)]" />
          <p className="font-medium">Unable to load extraction analytics</p>
          <p className="text-sm mt-2">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-[var(--color-brand)] text-white rounded-lg hover:bg-[var(--color-brand-hover)]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const pieData = Object.entries(data.cost_by_tier)
    .filter(([_, stats]) => stats.count > 0)
    .map(([tier, stats]) => ({
      name: TIER_LABELS[tier] || tier,
      value: stats.count,
      cost: stats.cost,
      color: TIER_COLORS[tier] || TIER_COLORS.unknown,
    }))
    .sort((a, b) => b.value - a.value);

  // Prepare daily trend data
  const dailyTrendData = data.cost_by_day.map(d => ({
    ...d,
    dateLabel: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  // Calculate problem files (files that needed paid tiers)
  const problemFiles = (data.cost_by_tier.claude_ocr?.count || 0) + (data.cost_by_tier.textract?.count || 0);
  const problemRate = data.total_uploads > 0 ? (problemFiles / data.total_uploads) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="extraction-analytics">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] flex items-center gap-2">
            <Database className="w-6 h-6 text-[var(--color-brand)]" />
            Extraction Pipeline Analytics
          </h2>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Document processing costs, methods, and performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="px-3 py-2 border border-[var(--color-border)] rounded-lg text-sm bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button
            onClick={fetchData}
            className="p-2 hover:bg-[var(--color-bg-hover)] rounded-lg transition"
          >
            <RefreshCw className="w-4 h-4 text-[var(--color-text-secondary)]" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[var(--color-bg-elevated)] rounded-xl p-4 border border-[var(--color-border)]">
          <div className="flex items-center gap-2 text-[var(--color-brand)] mb-2">
            <FileText className="w-5 h-5" />
            <span className="text-sm font-medium">Total Files</span>
          </div>
          <p className="text-3xl font-bold text-[var(--color-text-primary)]">{data.total_uploads.toLocaleString()}</p>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">Last {selectedPeriod} days</p>
        </div>

        <div className="bg-[var(--color-bg-elevated)] rounded-xl p-4 border border-[var(--color-border)]">
          <div className="flex items-center gap-2 text-[var(--color-verified)] mb-2">
            <DollarSign className="w-5 h-5" />
            <span className="text-sm font-medium">Total Cost</span>
          </div>
          <p className="text-3xl font-bold text-[var(--color-text-primary)]">${data.total_cost_usd.toFixed(2)}</p>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">${data.avg_cost_per_upload.toFixed(4)}/file avg</p>
        </div>

        <div className="bg-[var(--color-bg-elevated)] rounded-xl p-4 border border-[var(--color-border)]">
          <div className="flex items-center gap-2 text-[var(--color-verified)] mb-2">
            <Zap className="w-5 h-5" />
            <span className="text-sm font-medium">Free Processing</span>
          </div>
          <p className="text-3xl font-bold text-[var(--color-text-primary)]">{data.free_tier_percentage.toFixed(1)}%</p>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">Local OCR success rate</p>
        </div>

        <div className={`bg-[var(--color-bg-elevated)] rounded-xl p-4 border border-[var(--color-border)] ${
          problemRate > 20 ? 'border-[var(--color-text-muted)]' :
          problemRate > 10 ? 'border-[var(--color-text-secondary)]' :
          ''
        }`}>
          <div className={`flex items-center gap-2 mb-2 ${
            problemRate > 20 ? 'text-[var(--color-text-muted)]' :
            problemRate > 10 ? 'text-[var(--color-text-secondary)]' :
            'text-[var(--color-brand)]'
          }`}>
            <Cloud className="w-5 h-5" />
            <span className="text-sm font-medium">Paid API Usage</span>
          </div>
          <p className={`text-3xl font-bold ${
            problemRate > 20 ? 'text-[var(--color-text-muted)]' :
            problemRate > 10 ? 'text-[var(--color-text-secondary)]' :
            'text-[var(--color-text-primary)]'
          }`}>{problemRate.toFixed(1)}%</p>
          <p className={`text-xs mt-1 ${
            problemRate > 20 ? 'text-[var(--color-text-muted)]' :
            problemRate > 10 ? 'text-[var(--color-text-secondary)]' :
            'text-[var(--color-text-muted)]'
          }`}>{problemFiles} files needed paid OCR</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trends Chart */}
        <div className="bg-white rounded-xl border border-[var(--color-border)] p-6">
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Daily Extraction Volume & Cost</h3>
          {dailyTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dailyTrendData}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_PALETTE[0]} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CHART_PALETTE[0]} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_DONE_COLOR} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CHART_DONE_COLOR} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                <XAxis dataKey="dateLabel" tick={CHART_AXIS_STYLE} />
                <YAxis yAxisId="left" tick={CHART_AXIS_STYLE} />
                <YAxis yAxisId="right" orientation="right" tick={CHART_AXIS_STYLE} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'Files Processed' || name.toLowerCase().includes('file')) {
                      return [`${value} files`, 'Files Processed'];
                    } else {
                      return [`$${value.toFixed(3)}`, 'Daily Cost'];
                    }
                  }}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                />
                <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                <Area yAxisId="left" type="monotone" dataKey="count" stroke={CHART_PALETTE[0]} fill="url(#colorCount)" name="Files Processed" />
                <Line yAxisId="right" type="monotone" dataKey="cost" stroke={CHART_DONE_COLOR} strokeWidth={2} dot={false} name="Daily Cost" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-[var(--color-text-muted)]">
              No data available for selected period
            </div>
          )}
        </div>

        {/* Extraction Method Distribution */}
        <div className="bg-white rounded-xl border border-[var(--color-border)] p-6">
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Extraction Method Distribution</h3>
          {pieData.length > 0 ? (
            <div className="flex">
              <ResponsiveContainer width="50%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => [`${value} files`, 'Count']}
                    contentStyle={CHART_TOOLTIP_STYLE}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-1/2 pl-4 space-y-2 overflow-y-auto max-h-[280px]">
                {pieData.map((tier, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }} />
                      <span className="text-[var(--color-text-secondary)] truncate max-w-[140px]">{tier.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium text-[var(--color-text-primary)]">{tier.value}</span>
                      {tier.cost > 0 && (
                        <span className="text-[var(--color-text-muted)] ml-1">(${tier.cost.toFixed(2)})</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-[var(--color-text-muted)]">
              No extraction data available
            </div>
          )}
        </div>
      </div>

      {/* Tier Details Table */}
      <div className="bg-white rounded-xl border border-[var(--color-border)] p-6">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Extraction Tier Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-3 px-4 font-medium text-[var(--color-text-muted)]">Tier</th>
                <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">Files</th>
                <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">Cost</th>
                <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">Avg Cost</th>
                <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">% of Total</th>
                <th className="text-left py-3 px-4 font-medium text-[var(--color-text-muted)]">Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.cost_by_tier)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([tier, stats]) => {
                  const pct = data.total_uploads > 0 ? (stats.count / data.total_uploads) * 100 : 0;
                  const avgCost = stats.count > 0 ? stats.cost / stats.count : 0;
                  const isFree = avgCost === 0;

                  return (
                    <tr key={tier} className="border-b border-[var(--color-border-faint)] hover:bg-[var(--color-bg-hover)]">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: TIER_COLORS[tier] || TIER_COLORS.unknown }}
                          />
                          <span className="font-medium text-[var(--color-text-primary)]">{TIER_LABELS[tier] || tier}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-[var(--color-text-primary)]">{stats.count.toLocaleString()}</td>
                      <td className="text-right py-3 px-4 font-mono">
                        {isFree ? (
                          <span className="text-[var(--color-verified)]">Free</span>
                        ) : (
                          <span className="text-[var(--color-text-primary)]">${stats.cost.toFixed(2)}</span>
                        )}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-[var(--color-text-muted)]">
                        {isFree ? '-' : `$${avgCost.toFixed(4)}`}
                      </td>
                      <td className="text-right py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-[var(--color-bg-hover)] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: TIER_COLORS[tier] || TIER_COLORS.unknown
                              }}
                            />
                          </div>
                          <span className="text-[var(--color-text-muted)] w-12 text-right">{pct.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {isFree ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-transparent text-[var(--color-verified)] rounded-full text-xs">
                            <Zap className="w-3 h-3" /> Local
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] rounded-full text-xs">
                            <Cloud className="w-3 h-3" /> API
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Case-Level Breakdown (Expandable) */}
      {caseData.length > 0 && (
        <div className="bg-white rounded-xl border border-[var(--color-border)]">
          <button
            onClick={() => setShowCaseBreakdown(!showCaseBreakdown)}
            className="w-full flex items-center justify-between p-6 hover:bg-[var(--color-bg-hover)] transition"
          >
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-[var(--color-text-secondary)]" />
              <span className="text-lg font-semibold text-[var(--color-text-primary)]">Case-Level Breakdown</span>
              <span className="px-2 py-0.5 bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] text-sm rounded-full">
                {caseData.length} cases
              </span>
            </div>
            {showCaseBreakdown ? (
              <ChevronUp className="w-5 h-5 text-[var(--color-text-dim)]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[var(--color-text-dim)]" />
            )}
          </button>

          {showCaseBreakdown && (
            <div className="border-t border-[var(--color-border)] p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left py-3 px-4 font-medium text-[var(--color-text-muted)]">Case</th>
                      <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">Files</th>
                      <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">Cost</th>
                      <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">Success Rate</th>
                      <th className="text-left py-3 px-4 font-medium text-[var(--color-text-muted)]">Primary Method</th>
                      <th className="text-right py-3 px-4 font-medium text-[var(--color-text-muted)]">Problem Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseData
                      .sort((a, b) => b.total_cost - a.total_cost)
                      .slice(0, 20)
                      .map((caseItem) => {
                        const primaryMethod = Object.entries(caseItem.methods_used || {})
                          .sort(([, a], [, b]) => b - a)[0];

                        return (
                          <tr key={caseItem.case_id} className="border-b border-[var(--color-border-faint)] hover:bg-[var(--color-bg-hover)]">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-[var(--color-text-primary)]">{caseItem.client_name || 'Unknown'}</p>
                                <p className="text-xs text-[var(--color-text-muted)]">{caseItem.case_id.slice(0, 8)}...</p>
                              </div>
                            </td>
                            <td className="text-right py-3 px-4 font-mono text-[var(--color-text-primary)]">{caseItem.total_files}</td>
                            <td className="text-right py-3 px-4 font-mono">
                              ${caseItem.total_cost.toFixed(2)}
                            </td>
                            <td className="text-right py-3 px-4">
                              <span className={`font-medium ${
                                caseItem.success_rate >= 90 ? 'text-[var(--color-verified)]' :
                                caseItem.success_rate >= 70 ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-muted)]'
                              }`}>
                                {caseItem.success_rate.toFixed(0)}%
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {primaryMethod && (
                                <span
                                  className="px-2 py-1 rounded-full text-xs"
                                  style={{
                                    backgroundColor: `${TIER_COLORS[primaryMethod[0]] || TIER_COLORS.unknown}20`,
                                    color: TIER_COLORS[primaryMethod[0]] || TIER_COLORS.unknown
                                  }}
                                >
                                  {primaryMethod[0]}
                                </span>
                              )}
                            </td>
                            <td className="text-right py-3 px-4">
                              {caseItem.problem_files > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                                  <AlertTriangle className="w-3 h-3" />
                                  {caseItem.problem_files}
                                </span>
                              ) : (
                                <span className="text-[var(--color-verified)]">✓</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cost Savings Note */}
      {data.free_tier_percentage > 50 && (
        <div className="bg-transparent border border-[var(--color-verified)]/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-[var(--color-verified)] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-[var(--color-verified)]">Excellent Cost Efficiency!</h4>
              <p className="text-sm text-[var(--color-verified)] mt-1">
                {data.free_tier_percentage.toFixed(1)}% of documents processed using free local OCR.
                {data.total_uploads > 0 && data.paid_tier_percentage > 0 && (
                  <> Estimated savings: <strong>${((data.total_uploads * 0.04) - data.total_cost_usd).toFixed(2)}</strong> vs. all-Claude processing.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
